import React, { useState, useRef, useEffect } from "react";
import ReactPlayer from "react-player";
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute, FaFolder, FaBook, FaExpand, FaCompress, FaArrowLeft } from "react-icons/fa";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { config } from "../../config";
import axios from "axios";
import Comment from "./Comment";

const LessonPage = ({ course, module, lesson, lessonIndex, onBack }) => {
  const apiUrl =config.apiUrl;

const userId = localStorage.getItem('id');

  const [currentLessonIndex, setCurrentLessonIndex] = useState(lessonIndex);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(
    course.modules.findIndex(m => m.id === module.id)
  );
  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [expandedModules, setExpandedModules] = useState([module.id]);
  
  const playerRef = useRef(null);
  const videoContainerRef = useRef(null);
  const controlTimeoutRef = useRef(null);

  const currentModule = course.modules[currentModuleIndex];
  const currentLesson = currentModule.lessons[currentLessonIndex] || {};


  // Toggle module expansion
  const toggleModule = (moduleId) => {
    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  // Handle lesson selection
  const handleLessonSelect = (moduleIndex, lessonIndex) => {
    setCurrentModuleIndex(moduleIndex);
    setCurrentLessonIndex(lessonIndex);
    setPlaying(true);
    setIsReady(false);
    
    // Expand the module if it's collapsed
    const moduleId = course.modules[moduleIndex].id;
    if (!expandedModules.includes(moduleId)) {
      setExpandedModules(prev => [...prev, moduleId]);
    }
  };

  // Player control functions
  const handlePlayPause = () => {
    setPlaying(!playing);
    showControlPanel();
  };

  const handleProgress = (state) => {
    setPlayed(state.played);
    if (!isReady) setIsReady(true);
  };

  const handleSeek = (e) => {
    playerRef.current.seekTo(parseFloat(e.target.value));
    showControlPanel();
  };

  const handleNext = () => {
    if (currentLessonIndex < currentModule.lessons.length - 1) {
      // Next lesson in same module
      handleLessonSelect(currentModuleIndex, currentLessonIndex + 1);
    } else if (currentModuleIndex < course.modules.length - 1) {
      // First lesson of next module
      handleLessonSelect(currentModuleIndex + 1, 0);
    }
  };

  const handlePrevious = () => {
    if (currentLessonIndex > 0) {
      // Previous lesson in same module
      handleLessonSelect(currentModuleIndex, currentLessonIndex - 1);
    } else if (currentModuleIndex > 0) {
      // Last lesson of previous module
      const prevModule = course.modules[currentModuleIndex - 1];
      handleLessonSelect(currentModuleIndex - 1, prevModule.lessons.length - 1);
    }
  };

  const toggleFullscreen = () => {
    if (!fullscreen) {
      videoContainerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setFullscreen(!fullscreen);
    showControlPanel();
  };

  const showControlPanel = () => {
    setShowControls(true);
    clearTimeout(controlTimeoutRef.current);
    controlTimeoutRef.current = setTimeout(() => {
      if (playing) {
        setShowControls(false);
      }
    }, 3000);
  };

  const handleMouseMove = () => {
    showControlPanel();
  };

  const handleTouchStart = () => {
    showControlPanel();
  };

  useEffect(() => {
    setPlaying(true);
    showControlPanel();
  }, [currentLessonIndex, currentModuleIndex]);

  // Rest of your existing useEffect hooks...

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-2 md:px-4 lg:px-8 py-6">
        {/* Header with breadcrumbs */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <button
            onClick={onBack}
            className="flex items-center text-indigo-600 hover:text-indigo-800 mb-4 md:mb-0"
          >
            <FaArrowLeft className="mr-2" />
            <span>Back to Modules</span>
          </button>
          
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span className="font-medium text-indigo-600">{course?.name}</span>
            <FiChevronRight className="text-xs" />
            <span>{currentLesson?.title || "Lesson"}</span>
          </div>
        </div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Video player and content (3/4 width) */}
          <div className="lg:col-span-3 space-y-6">
            {/* Video player container */}
            <div 
              ref={videoContainerRef}
              className="relative bg-black rounded-xl shadow-xl overflow-hidden"
              style={{ paddingTop: '56.25%' }}
              onMouseMove={handleMouseMove}
              onTouchStart={handleTouchStart}
            >
              {/* Loading indicator */}
              {(!isReady || isBuffering) && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-20">
                  <div className="h-12 w-12 border-4 border-t-indigo-500 border-r-indigo-500 border-b-transparent border-l-transparent rounded-full animate-spin" />
                </div>
              )}

              {/* Video player */}
              <div>
                <ReactPlayer
                  ref={playerRef}
                  url={currentLesson?.videoUrl || ""}
                  playing={playing}
                  volume={volume}
                  muted={muted}
                  onProgress={handleProgress}
                  onReady={() => setIsReady(true)}
                  onBuffer={() => setIsBuffering(true)}
                  onBufferEnd={() => setIsBuffering(false)}
                  width="100%"
                  height="100%"
                  className="absolute top-0 left-0"
                  config={{
                    youtube: {
                      playerVars: { 
                        modestbranding: 1,
                        rel: 0,
                        showinfo: 0,
                        controls: 0,
                        disablekb: 1
                      }
                    },
                    file: {
                      attributes: {
                        preload: 'auto',
                        disablePictureInPicture: true
                      }
                    }
                  }}
                />
                <div className="absolute inset-0 bg-black opacity-0 hover:opacity-25 transition duration-300"></div>
                {!playing && (
                  <div
                    className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 cursor-pointer"
                    onClick={handlePlayPause}
                  >
                    <FaPlay className="text-white text-2xl z-50" />
                  </div>
                )}
              </div>

              {/* Control panel */}
              <div 
                className={`absolute bottom-0 left-0 right-0 transition-opacity duration-300 ${(showControls || !playing) ? 'opacity-100' : 'opacity-0'} z-10`}
              >
                <div className="bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
                  {/* Bottom controls */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={handlePlayPause}
                        className="p-2 text-white hover:bg-white/20 rounded-full transition"
                      >
                        {playing ? <FaPause size={20} /> : <FaPlay size={20} />}
                      </button>
                      {/* Progress bar */}
                      <div className="w-full mb-2">
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step="0.001"
                          value={played}
                          onChange={handleSeek}
                          className="w-12 md:w-48 h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => setMuted(!muted)}
                          className="p-2 text-white hover:bg-white/20 rounded-full transition"
                        >
                          {muted ? <FaVolumeMute size={20} /> : <FaVolumeUp size={20} />}
                        </button>
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step="0.01"
                          value={volume}
                          onChange={(e) => {
                            setVolume(parseFloat(e.target.value));
                            showControlPanel();
                          }}
                          className="w-6 md:w-12 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={toggleFullscreen}
                        className="p-2 text-white hover:bg-white/20 rounded-full transition"
                      >
                        {fullscreen ? <FaCompress size={18} /> : <FaExpand size={18} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lesson navigation */}
            <div className="flex justify-between">
              <button
                onClick={handlePrevious}
                disabled={currentModuleIndex === 0 && currentLessonIndex === 0}
                className={`flex items-center px-4 py-2 rounded-lg transition ${
                  currentModuleIndex === 0 && currentLessonIndex === 0 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                <FiChevronLeft className="mr-2" />
                Previous Lesson
              </button>
              
              <button
                onClick={handleNext}
                disabled={
                  currentModuleIndex === course.modules.length - 1 && 
                  currentLessonIndex === currentModule.lessons.length - 1
                }
                className={`flex items-center px-4 py-2 rounded-lg transition ${
                  currentModuleIndex === course.modules.length - 1 && 
                  currentLessonIndex === currentModule.lessons.length - 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                Next Lesson
                <FiChevronRight className="ml-2" />
              </button>
            </div>

            {/* Lesson details */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h1 className="text-2xl font-bold text-gray-800 mb-4">{currentLesson?.title}</h1>
              <div className="prose max-w-none text-gray-700">
                <div dangerouslySetInnerHTML={{ __html: currentLesson.description}} />
                
              </div>
              
              {currentLesson.resources && (
                <div className="mt-6">
                  <h2 className="text-xl font-semibold mb-3 text-gray-800">Resources</h2>
                  <div className="bg-gray-50 rounded-lg p-4">
                     <div dangerouslySetInnerHTML={{ __html: currentLesson.resources}} />
                    
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Lessons sidebar (1/4 width) - Now shows all modules */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md overflow-hidden sticky top-6 max-h-[calc(100vh-3rem)] overflow-y-auto">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">Course Content</h2>
              </div>
              
              <div className="divide-y divide-gray-200">
                {course.modules.map((module, modIndex) => (
                  <div key={module.id} className="p-4">
                    <button
                      onClick={() => toggleModule(module.id)}
                      className="w-full flex justify-between items-center text-left mb-2"
                    >
                      <div className="flex items-center text-indigo-600 font-medium">
                        <FaFolder className="mr-3" />
                        <span>{module.moduleTitle}</span>
                      </div>
                      <FiChevronRight 
                        className={`transition-transform duration-200 ${
                          expandedModules.includes(module.id) ? 'transform rotate-90' : ''
                        }`} 
                      />
                    </button>
                    
                    {expandedModules.includes(module.id) && (
                      <ul className="space-y-2 pl-8">
                        {module.lessons.map((lesson, lessonIndex) => (
                          <li key={lessonIndex}>
                            <button
                              onClick={() => handleLessonSelect(modIndex, lessonIndex)}
                              className={`w-full text-left p-2 rounded-md flex items-center transition ${
                                currentModuleIndex === modIndex && currentLessonIndex === lessonIndex
                                  ? 'bg-indigo-50 text-indigo-600 font-medium' 
                                  : 'text-gray-600 hover:bg-gray-100'
                              }`}
                            >
                              <FaPlay className="mr-3 text-indigo-400" />
                              <span className="truncate">{lesson.title}</span>
                              {currentModuleIndex === modIndex && currentLessonIndex === lessonIndex && (
                                <span className="ml-auto h-2 w-2 bg-indigo-600 rounded-full"></span>
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
       {/* Comment Section - Replace your existing comment section with this */}
        <div className="max-w-7xl max-h-screen overflow-y-auto overflow-x-auto mx-auto px-2 md:px-4 lg:px-8 py-6">
          {/* Comment Section */}
          <Comment 
            userId={userId}
            lessonId={lesson.id}
            apiUrl={apiUrl}
          />
        </div>
      </div>
    </div>
  );
};

export default LessonPage;